import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import indlusLogo from '../../../assets/indlusr.png'
import EmailSentConfirmation from '../../auth/components/EmailSentConfirmation'
import { api } from '../../../shared/services/API'

interface FormData {
    email: string
}

export default function AdminForgotPassword() {
    const [sentTo, setSentTo] = useState('')
    const [apiError, setApiError] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    const { register, handleSubmit, formState: { errors } } = useForm<FormData>()

    const onSubmit = async (data: FormData) => {
        setApiError('')
        setIsSubmitting(true)
        try {
            // O backend responde sempre 204, exista ou não a conta — não revela
            // se o email pertence a um administrador. Só um erro de rede/servidor
            // cai no catch.
            await api.post('/api/admin/auth/forgot-password', { email: data.email })
            setSentTo(data.email)
        } catch {
            setApiError('Ocorreu um erro. Tente novamente mais tarde.')
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                bgcolor: 'background.default',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
                gap: 3,
            }}
        >
            <Box component="img" src={indlusLogo} alt="Indlus" sx={{ height: 44, width: 'auto' }} />

            <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, border: '1px solid', borderColor: 'divider', width: '100%', maxWidth: 400 }}>
                {sentTo ? (
                    <EmailSentConfirmation email={sentTo} onResend={() => setSentTo('')} />
                ) : (
                    <>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                            Recuperar palavra-passe
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Introduza o email da sua conta de administrador para redefinir a senha.
                        </Typography>

                        {apiError && (
                            <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                                {apiError}
                            </Alert>
                        )}

                        <Box
                            component="form"
                            onSubmit={handleSubmit(onSubmit)}
                            noValidate
                            sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
                        >
                            <TextField
                                label="Email"
                                type="email"
                                autoFocus
                                autoComplete="username"
                                error={!!errors.email}
                                helperText={errors.email?.message}
                                {...register('email', {
                                    required: 'O email é obrigatório.',
                                    pattern: {
                                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                        message: 'Insira um email válido.',
                                    },
                                })}
                            />

                            <Button type="submit" variant="contained" size="large" fullWidth disabled={isSubmitting}>
                                {isSubmitting ? 'A enviar…' : 'Enviar link'}
                            </Button>
                        </Box>

                        <Box sx={{ mt: 3, textAlign: 'center' }}>
                            <Link
                                to="/login"
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    color: '#64748B',
                                    textDecoration: 'none',
                                    fontSize: '0.875rem',
                                }}
                            >
                                <ArrowBackIcon sx={{ fontSize: 16 }} />
                                Voltar ao login
                            </Link>
                        </Box>
                    </>
                )}
            </Paper>
        </Box>
    )
}
